import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
  OAuthApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

import {
  CreateGroupMemberRequest,
  CreateGroupRequest,
  CreateUserMetadataRequest,
  Group,
  GroupMember,
  TeamsChannel,
  UpdateGroupMemberRequest,
  UpdateGroupRequest,
  UpdateUserMetadataRequest,
  UserMetadata,
} from '@internal/backstage-plugin-org-manager-common';

import { OrgManagerApi } from './OrgManagerApi';

export class OrgManagerClient implements OrgManagerApi {
  private baseUrl?: Promise<string>;

  private constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly identityApi: IdentityApi,
    private readonly fetchApi: FetchApi,
    private readonly microsoftAuthApi: OAuthApi,
  ) {}

  private getBaseUrl(): Promise<string> {
    return (this.baseUrl ??= this.discoveryApi.getBaseUrl('org-manager'));
  }

  public static create({
    discoveryApi,
    identityApi,
    fetchApi,
    microsoftAuthApi,
  }: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
    fetchApi: FetchApi;
    microsoftAuthApi: OAuthApi;
  }): OrgManagerClient {
    return new OrgManagerClient(
      discoveryApi,
      identityApi,
      fetchApi,
      microsoftAuthApi,
    );
  }

  public async getGroups(): Promise<Group[]> {
    return this.fetch<Group[]>('/groups');
  }

  public async getTeamsChannels(): Promise<TeamsChannel[]> {
    return this.fetch<TeamsChannel[]>('/teams/channels', {
      headers: {
        'X-Microsoft-Access-Token': await this.microsoftAuthApi.getAccessToken(
          'Channel.ReadBasic.All',
        ),
      },
    });
  }

  public async getGroup(id: string): Promise<Group> {
    return this.fetch<Group>(`/groups/${id}`);
  }

  public async createGroup(request: CreateGroupRequest): Promise<Group> {
    return this.fetch<Group>('/groups', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(request.teamsChannelId
          ? {
              'X-Microsoft-Access-Token':
                await this.microsoftAuthApi.getAccessToken(
                  'Channel.ReadBasic.All',
                ),
            }
          : {}),
      },
      body: JSON.stringify(request),
    });
  }

  public async updateGroup(
    id: string,
    request: UpdateGroupRequest,
  ): Promise<Group> {
    return this.fetch<Group>(`/groups/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(request.teamsChannelId
          ? {
              'X-Microsoft-Access-Token':
                await this.microsoftAuthApi.getAccessToken(
                  'Channel.ReadBasic.All',
                ),
            }
          : {}),
      },
      body: JSON.stringify(request),
    });
  }

  public async deleteGroup(id: string): Promise<void> {
    await this.fetch<void>(`/groups/${id}`, { method: 'DELETE' });
  }

  public async getGroupMembers(groupId: string): Promise<GroupMember[]> {
    return this.fetch<GroupMember[]>(`/groups/${groupId}/members`);
  }

  public async getGroupMember(
    groupId: string,
    userEntityRef: string,
  ): Promise<GroupMember> {
    return this.fetch<GroupMember>(
      `/groups/${groupId}/members/${encodeURIComponent(userEntityRef)}`,
    );
  }

  public async createGroupMember(
    groupId: string,
    request: CreateGroupMemberRequest,
  ): Promise<GroupMember> {
    return this.fetch<GroupMember>(`/groups/${groupId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  }

  public async updateGroupMember(
    groupId: string,
    userEntityRef: string,
    request: UpdateGroupMemberRequest,
  ): Promise<GroupMember> {
    return this.fetch<GroupMember>(
      `/groups/${groupId}/members/${encodeURIComponent(userEntityRef)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
  }

  public async deleteGroupMember(
    groupId: string,
    userEntityRef: string,
  ): Promise<void> {
    await this.fetch<void>(
      `/groups/${groupId}/members/${encodeURIComponent(userEntityRef)}`,
      { method: 'DELETE' },
    );
  }

  public async getAllUserMetadata(): Promise<UserMetadata[]> {
    return this.fetch<UserMetadata[]>('/users/metadata');
  }

  public async getUserMetadata(userEntityRef: string): Promise<UserMetadata> {
    return this.fetch<UserMetadata>(
      `/users/${encodeURIComponent(userEntityRef)}/metadata`,
    );
  }

  public async createUserMetadata(
    userEntityRef: string,
    request: CreateUserMetadataRequest,
  ): Promise<UserMetadata> {
    return this.fetch<UserMetadata>(
      `/users/${encodeURIComponent(userEntityRef)}/metadata`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
  }

  public async updateUserMetadata(
    userEntityRef: string,
    request: UpdateUserMetadataRequest,
  ): Promise<UserMetadata> {
    return this.fetch<UserMetadata>(
      `/users/${encodeURIComponent(userEntityRef)}/metadata`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
  }

  public async deleteUserMetadata(userEntityRef: string): Promise<void> {
    await this.fetch<void>(
      `/users/${encodeURIComponent(userEntityRef)}/metadata`,
      { method: 'DELETE' },
    );
  }

  private async fetch<T = any>(input: string, init?: RequestInit): Promise<T> {
    const [baseUrl, { token }] = await Promise.all([
      this.getBaseUrl(),
      this.identityApi.getCredentials(),
    ]);

    const headers: HeadersInit = new Headers(init?.headers);

    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return this.fetchApi
      .fetch(`${baseUrl}${input}`, { ...init, headers })
      .then(async response => {
        if (!response.ok) {
          throw await ResponseError.fromResponse(response);
        }
        if (response.status === 204) {
          return undefined as T;
        }
        return response.json() as Promise<T>;
      });
  }
}
